import { EyeOutlined } from "@ant-design/icons";
import { Button, Descriptions, Drawer, Empty, Flex, Grid, Image, notification, Skeleton, Tag, Typography } from "antd";
import { useGetOneSalesOrderV2Query, useLazyGetSalesOrderInvoicePDFQuery } from "api/sales-order/sales-order-api";
import { OBJ_COLOR } from "commons/constants";
import { getColorStatusSalesOrder, StatusSalesOrder } from "commons/constants/sales-order";
import { colorInvoiceStatus, dateFormat, getChickenTypeLabel, getUrlImage, translationInvoiceStatus } from "commons/constants/util";
import useLoading from "hooks/useLoading";
import { useState } from "react";
import SalesOrderDetailTableV2 from "./SalesOrderDetailTableV2";

const { useBreakpoint } = Grid;

interface SeeSalesOrderDrawerProps {
	open: boolean;
	onClose: () => void;
	salesOrderId: number;
}

const SeeSalesOrderDrawerV2 = ({ open, onClose, salesOrderId }: SeeSalesOrderDrawerProps) => {
	const { isLoading } = useLoading();

	const screens = useBreakpoint();

	const { data: dataSalesOrder, isFetching } = useGetOneSalesOrderV2Query(salesOrderId, { skip: !salesOrderId, refetchOnMountOrArgChange: true });

	const [getSalesOrderInvoicePDF] = useLazyGetSalesOrderInvoicePDFQuery();

	const [urlImage, setUrlImage] = useState("");

	if (!dataSalesOrder) return null;

	const {
		customerName,
		customerId,
		custPhone,
		orderDate,
		farmCode,
		farmName,
		shipDate,
		shipAddress,
		driver,
		driverPhone,
		status,
		truckId,
		pathImage,
		chickenType,
		itemCode,
		itemName,
		gender,
		deliveryNoteName,
		invoiceStatus,
		note,
		invoiceNo,
		invoiceDate,
		billingNo,
	} = dataSalesOrder;

	const pathImageList = pathImage.split("|").filter((src) => src);

	const handleOnClick = async () => {
		try {
			const result = await getSalesOrderInvoicePDF(dataSalesOrder.fkey).unwrap();

			if (result.data) {
				console.log(result.data);
				setUrlImage(result.data);
			}
		} catch (error) {
			notification.error({
				message: "Vui lòng thử lại sau.",
			});
		}
	};

	return (
		<Drawer title={`Thông tin đơn hàng`} open={open} width={"100%"} onClose={onClose}>
			<Flex vertical gap={20} style={{ width: screens.xxl ? "60%" : screens.xxl ? "90%" : "99%", margin: "auto" }}>
				<Flex vertical gap={20}>
					<Flex justify="space-between" align="center">
						<Typography.Text style={{ color: OBJ_COLOR.SUCCESS, fontSize: 16, fontWeight: 600 }}>Thông tin đơn hàng</Typography.Text>
					</Flex>
					<Skeleton active loading={isFetching}>
						<div
							style={{
								padding: 10,
								border: "1px solid #efefef",
								borderRadius: 8,
							}}>
							<Descriptions column={{ xs: 1, xl: 2, xxl: 3 }} size="small" style={{ fontWeight: 500 }}>
								<Descriptions.Item label="Mã đơn hàng">
									<Typography.Text type="success" strong>
										{salesOrderId}
									</Typography.Text>
								</Descriptions.Item>
								<Descriptions.Item label="Ngày tạo">{dateFormat(orderDate)}</Descriptions.Item>
								<Descriptions.Item label="Ngày giao hàng">{dateFormat(shipDate)}</Descriptions.Item>
								<Descriptions.Item label="Trại">{`[${farmCode}]: ${farmName}`}</Descriptions.Item>
								<Descriptions.Item label="Khách hàng">{`[${customerId}]: ${customerName}`}</Descriptions.Item>
								<Descriptions.Item label="SĐT khách hàng">{custPhone}</Descriptions.Item>
								<Descriptions.Item label="Địa chỉ giao hàng">{shipAddress}</Descriptions.Item>
								<Descriptions.Item label="Tên tài xế">{driver || "-"}</Descriptions.Item>
								<Descriptions.Item label="SĐT tài xế">{driverPhone || "-"}</Descriptions.Item>
								<Descriptions.Item label="Số xe">{truckId || "-"}</Descriptions.Item>
								<Descriptions.Item label="Con giống">{`[${itemCode}]: ${itemName}`}</Descriptions.Item>
								<Descriptions.Item label="Giới tính">{gender.join(" | ")}</Descriptions.Item>
								<Descriptions.Item label="Gà loại">{getChickenTypeLabel(itemCode, chickenType, deliveryNoteName)}</Descriptions.Item>
								<Descriptions.Item label="Ghi chú">{note}</Descriptions.Item>
								<Descriptions.Item label="Trạng thái đơn hàng">{<Tag color={getColorStatusSalesOrder(status as StatusSalesOrder)}>{status}</Tag>}</Descriptions.Item>
								<Descriptions.Item label="Ngày hóa đơn">{invoiceDate ? dateFormat(invoiceDate) : "-"}</Descriptions.Item>
								<Descriptions.Item label="Số hóa đơn">{invoiceNo}</Descriptions.Item>
								<Descriptions.Item label="BillingNo">{billingNo}</Descriptions.Item>
								<Descriptions.Item label="Trạng thái hoá đơn">{<Tag color={colorInvoiceStatus(invoiceStatus)}>{translationInvoiceStatus(invoiceStatus)}</Tag>}</Descriptions.Item>
							</Descriptions>
						</div>
					</Skeleton>
				</Flex>

				<Flex vertical>
					<Typography.Text style={{ color: OBJ_COLOR.SUCCESS, fontSize: 16, fontWeight: 600 }}>Hình ảnh</Typography.Text>
					<Flex gap={20} wrap="wrap" style={{ marginTop: 10 }}>
						{pathImageList.length ? (
							pathImageList.map((src) => <Image style={{ border: "1px solid rgb(239, 239, 239)", borderRadius: 6 }} width={100} height={100} src={getUrlImage(src)} />)
						) : (
							<Empty description="Không có hình ảnh" />
						)}
					</Flex>
				</Flex>

				<Flex vertical gap={20}>
					<Flex justify="space-between" align="center">
						<Typography.Text style={{ color: OBJ_COLOR.SUCCESS, fontSize: 16, fontWeight: 600 }}>Chi tiết đơn hàng</Typography.Text>
						{dataSalesOrder?.fkey && (
							<Button loading={isLoading} type="primary" ghost icon={<EyeOutlined />} onClick={handleOnClick}>
								Xem hoá đơn
							</Button>
						)}
					</Flex>
					<SalesOrderDetailTableV2 salesOrderId={salesOrderId} />
				</Flex>
			</Flex>

			{urlImage && (
				<Drawer title="Thông tin hoá đơn" open onClose={() => setUrlImage("")} footer={null} width="100vw" style={{ top: 0 }} bodyStyle={{ height: "100vh", padding: 0 }}>
					<iframe title="PDF Viewer" src={`${urlImage}#toolbar=0&navpanes=0`} width="100%" height="100%" style={{ border: "none" }} />
				</Drawer>
			)}
		</Drawer>
	);
};

export default SeeSalesOrderDrawerV2;
